import { useQuery } from '@apollo/client/react'
import { Search } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '~/components/ui/dialog'
import { GQL_MY_SESSIONS } from '~/gql/sessions/sessions'
import { tourAnchor } from '~/helpers/tour/anchors'
import { useIsMobile } from '~/hooks/use-mobile'
import { useSearchShortcut } from '~/hooks/use-search-shortcut'
import SearchCombobox from '~/pages/scorm/components/learner-search/SearchCombobox'

/** Le raccourci s'affiche avec la touche du système : ⌘ sur Mac, Ctrl ailleurs. */
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

/**
 * Recherche de l'en-tête apprenant (#109), sur `mySessions` : la requête de
 * l'accueil, déjà en cache. `cache-first` : l'accueil et le parcours la
 * rafraîchissent déjà ; la relancer ici coûtait une requête sur les écrans
 * Compte et Aide, pour une recherche que personne n'a encore ouverte.
 *
 * Sur mobile, l'en-tête n'a pas la place d'un champ : une icône ouvre la
 * recherche en plein écran, où le panneau de résultats a toute la hauteur.
 */
function LearnerSearch() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const { data } = useQuery(GQL_MY_SESSIONS, { fetchPolicy: 'cache-first' })
  const sessions = data?.mySessions ?? []

  useSearchShortcut(() => (isMobile ? setOpen(true) : inputRef.current?.focus()))

  if (!isMobile) {
    return (
      <div className="w-full max-w-md" {...tourAnchor('learner.search')}>
        <SearchCombobox
          sessions={sessions}
          onSelect={target => navigate(target)}
          inputRef={inputRef}
          keyShortcuts="Control+K Meta+K"
          hint={<kbd className="rounded border bg-muted px-1.5 text-xs text-muted-foreground" aria-hidden>{IS_MAC ? '⌘ K' : 'Ctrl K'}</kbd>}
          panelClassName="absolute inset-x-0 top-full z-50 mt-2 max-h-[70vh] overflow-y-auto shadow-lg"
        />
      </div>
    )
  }

  return (
    <div {...tourAnchor('learner.search')}>
      <Button
        variant="ghost"
        size="icon"
        aria-label={t('learnerSearch.label')}
        onClick={() => setOpen(true)}
      >
        <Search className="size-5" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        {/* Plein écran : une croix de 16 px dans le coin, collée au champ de
            recherche, se rate au doigt. Un vrai bouton « Fermer », à côté du
            champ, la remplace (#116). */}
        <DialogContent
          showCloseButton={false}
          aria-describedby={undefined}
          // Plein écran jusqu'à 767 px (`useIsMobile`) : la base du `Dialog`
          // arrondit ses coins dès `sm` et glisse depuis le centre, ce qui
          // décalait l'entrée entre 640 et 767 px.
          className="inset-0 left-0 top-0 flex h-dvh max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none p-4 data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0 data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0 data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100 sm:rounded-none"
        >
          <DialogTitle className="sr-only">{t('learnerSearch.label')}</DialogTitle>
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <SearchCombobox
                sessions={sessions}
                // Le dialogue de recherche s'ouvre pour qu'on y tape : le focus va au champ.
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                onSelect={(target) => {
                  setOpen(false)
                  navigate(target)
                }}
                panelClassName="mt-3 max-h-[calc(100dvh-6rem)] overflow-y-auto border-0 bg-transparent p-0"
              />
            </div>
            <Button variant="ghost" onClick={() => setOpen(false)}>{t('common.close')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default LearnerSearch
